import { ArrowUpRight } from 'lucide-react';
import { useLang } from '../lib/lang';
import { IMAGES, CONTACT, VERTIV_CERTS, ADDRESS_LABELS } from '../content/constants';
import { Page, HeroShell, Reveal, Overline } from '../components/motion';
import { SkeletonGrid, CTASection, PageSection, SectionHead } from '../components/blocks';

export default function About() {
  const { t, lang } = useLang();
  const c = t.about;
  const al = ADDRESS_LABELS[lang] || ADDRESS_LABELS.pt;
  return (
    <Page meta={{ ...t.meta.about, lang: t.lang }}>
      <HeroShell testId="about-hero" overline={c.hero.overline} lines={c.hero.lines} image={IMAGES.serverBlue} />

      <section data-testid="about-who" className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHead overline={c.who.overline} title={c.who.title} />
            <Reveal delay={0.15} className="mt-12 relative group overflow-hidden">
              <div className="absolute -inset-8 spotlight" aria-hidden="true" />
              <img src={IMAGES.serverBlue} alt="Data centre" className="relative img-treated w-full aspect-[4/3] object-cover border border-white/10" />
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-6 self-center">
            <Reveal><p className="text-base md:text-lg text-white leading-relaxed">{c.who.p1}</p></Reveal>
            <Reveal delay={0.08}><p className="text-base text-zinc-400 leading-relaxed">{c.who.p2}</p></Reveal>
            <Reveal delay={0.16}><p className="text-base text-zinc-400 leading-relaxed">{c.who.p3}</p></Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-mission-vision" className="border-t border-white/10 bg-surface/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-2 gap-12 md:gap-16">
          {[c.mission, c.vision].map((block, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <Overline>{block.t}</Overline>
              <p className="mt-6 font-display text-xl md:text-2xl font-medium tracking-tight leading-relaxed text-zinc-300">{block.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <PageSection overline={c.differentiators.overline} testId="about-differentiators">
        <SkeletonGrid items={c.differentiators.items} cols="md:grid-cols-3" numbered testId="differentiators-grid" />
      </PageSection>

      <PageSection overline={c.vertiv.overline} title={c.vertiv.title} testId="about-vertiv">
        <Reveal><p className="text-base text-zinc-400 mb-10">{c.vertiv.intro}</p></Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {c.vertiv.cards.map((card, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div data-testid={`vertiv-cert-card-${i}`} className="border border-volt/30 bg-surface hover:border-volt transition-colors duration-500">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10">
                  <span className="w-2 h-2 rounded-full bg-volt/70" />
                  <span className="w-2 h-2 rounded-full bg-white/20" />
                  <span className="w-2 h-2 rounded-full bg-white/20" />
                  <span className="ml-auto font-mono text-[10px] tracking-[0.2em] text-zinc-500">VERTIV // CERT-0{i + 1}</span>
                </div>
                <div className="p-8 md:p-10">
                  <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight text-white">{card.t}</h3>
                  <p className="mt-4 font-mono text-sm text-zinc-400 leading-relaxed">{card.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap gap-4">
            {VERTIV_CERTS.map((cert) => (
              <a
                key={cert.key}
                href={cert.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`vertiv-cert-link-${cert.key}`}
                className="group inline-flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white hover:border-volt hover:text-volt transition-colors duration-300"
              >
                {c.vertiv.certLink[cert.key]}
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-10 max-w-3xl text-sm text-zinc-500 leading-relaxed border-l-2 border-volt pl-6">{c.vertiv.note}</p>
        </Reveal>
      </PageSection>

      <section data-testid="about-legal" className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <Overline>{c.legal.overline}</Overline>
          <Reveal className="mt-6 font-mono text-sm text-zinc-400 leading-loose">
            <p className="text-white">{CONTACT.company}</p>
            <p><span className="text-zinc-500">{al.hq}: </span>{CONTACT.hq.line1}, {CONTACT.hq.line2}</p>
            <p><span className="text-zinc-500">{al.tech}: </span>{CONTACT.tech}</p>
            <p>NIF: {CONTACT.nif}</p>
          </Reveal>
        </div>
      </section>

      <CTASection title={t.home.cta.title} body={t.home.cta.body} btn={t.home.cta.btn} />
    </Page>
  );
}
